/**
 * Goals Actions
 *
 * Actions for managing goals state in the NGXS store.
 */

import { Goal } from 'src/app/models/schema';

/**
 * Action to initialize the goals store
 * Loads the first batch of goals from the partner's subcollection
 * @param partnerId - Partner ID (determines subcollection path: partners/{partnerId}/goals)
 */
export class InitializeGoals {
  static readonly type = '[Goals] Initialize';
  constructor(public partnerId: string) {}
}

/**
 * Action dispatched when initialization succeeds
 * @param goals - The initial batch of goals
 * @param hasMore - Whether there are more goals to load
 */
export class InitializeGoalsSuccess {
  static readonly type = '[Goals] Initialize Success';
  constructor(
    public goals: Goal[],
    public hasMore: boolean
  ) {}
}

/**
 * Action dispatched when initialization fails
 * @param error - The error message
 */
export class InitializeGoalsFailure {
  static readonly type = '[Goals] Initialize Failure';
  constructor(public error: string) {}
}

/**
 * Action to load the next batch of goals
 */
export class LoadNextBatch {
  static readonly type = '[Goals] Load Next Batch';
}

/**
 * Action dispatched when loading next batch succeeds
 * @param goals - The new batch of goals
 * @param hasMore - Whether there are more goals to load
 */
export class LoadNextBatchSuccess {
  static readonly type = '[Goals] Load Next Batch Success';
  constructor(
    public goals: Goal[],
    public hasMore: boolean
  ) {}
}

/**
 * Action dispatched when loading next batch fails
 * @param error - The error message
 */
export class LoadNextBatchFailure {
  static readonly type = '[Goals] Load Next Batch Failure';
  constructor(public error: string) {}
}

/**
 * Action to set filter criteria
 * @param filters - Filter options to apply
 */
export class SetGoalsFilters {
  static readonly type = '[Goals] Set Filters';
  constructor(public filters: any) {}
}

/**
 * Action to set sort criteria
 * @param sortField - Field to sort by
 * @param sortDirection - Sort direction (asc/desc)
 */
export class SetGoalsSort {
  static readonly type = '[Goals] Set Sort';
  constructor(
    public sortField: string,
    public sortDirection: 'asc' | 'desc'
  ) {}
}

/**
 * Action to add a new goal
 * @param goal - The goal to add
 */
export class AddGoal {
  static readonly type = '[Goals] Add Goal';
  constructor(public goal: Goal) {}
}

/**
 * Action dispatched when adding goal succeeds
 * @param goal - The added goal
 */
export class AddGoalSuccess {
  static readonly type = '[Goals] Add Goal Success';
  constructor(public goal: Goal) {}
}

/**
 * Action dispatched when adding goal fails
 * @param error - The error message
 */
export class AddGoalFailure {
  static readonly type = '[Goals] Add Goal Failure';
  constructor(public error: string) {}
}

/**
 * Action to update an existing goal
 * @param goal - The goal to update
 */
export class UpdateGoal {
  static readonly type = '[Goals] Update Goal';
  constructor(public goal: Goal) {}
}

/**
 * Action dispatched when updating goal succeeds
 * @param goal - The updated goal
 */
export class UpdateGoalSuccess {
  static readonly type = '[Goals] Update Goal Success';
  constructor(public goal: Goal) {}
}

/**
 * Action dispatched when updating goal fails
 * @param error - The error message
 */
export class UpdateGoalFailure {
  static readonly type = '[Goals] Update Goal Failure';
  constructor(public error: string) {}
}

/**
 * Action to delete a goal
 * @param goalId - The ID of the goal to delete
 */
export class DeleteGoal {
  static readonly type = '[Goals] Delete Goal';
  constructor(public goalId: string) {}
}

/**
 * Action dispatched when deleting goal succeeds
 * @param goalId - The ID of the deleted goal
 */
export class DeleteGoalSuccess {
  static readonly type = '[Goals] Delete Goal Success';
  constructor(public goalId: string) {}
}

/**
 * Action dispatched when deleting goal fails
 * @param error - The error message
 */
export class DeleteGoalFailure {
  static readonly type = '[Goals] Delete Goal Failure';
  constructor(public error: string) {}
}

/**
 * Action to update goals from real-time listener
 * @param goals - Updated goals from Firestore
 */
export class UpdateGoalsFromListener {
  static readonly type = '[Goals] Update From Listener';
  constructor(public goals: Goal[]) {}
}

/**
 * Action to clear all goals data
 * Used when switching teams or cleaning up
 */
export class ClearGoals {
  static readonly type = '[Goals] Clear';
}

/**
 * Action to clear error state
 */
export class ClearGoalsError {
  static readonly type = '[Goals] Clear Error';
}
